import { useState, useEffect } from 'react';
import { ChatInput } from '../chat/ChatInput';
import { ChatMessageList } from '../chat/ChatMessageList';
import { useChat } from '../../hooks/useChat';
import type { AgentConfig } from '@/lib/types';
import { extractSummaries } from '@/lib/summary-extractor';

interface ReflectionChatProps {
  learnerId: string;
  moduleId: string;
  partId: string;
  agentConfig: AgentConfig;
  onSummaryExtracted: (summary: string) => void;
  onComplete: () => void;
  onSkip: () => void;
}

/**
 * Render inline markdown: **bold** and *italic*
 */
function renderInlineMarkdown(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[2]) {
      // **bold**
      parts.push(<strong key={key++} className="font-semibold text-slate-900 dark:text-white">{match[2]}</strong>);
    } else if (match[3]) {
      // *italic*
      parts.push(<em key={key++}>{match[3]}</em>);
    }

    lastIndex = match.index + match[0].length;
  }

  // Remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

/**
 * Render welcome message with paragraph breaks and inline markdown
 */
function WelcomeMessage({ text }: { text: string }) {
  const paragraphs = text.trim().split(/\n\n+/);

  return (
    <div className="px-6 py-8">
      {/* Decorative prompt icon */}
      <div className="flex items-start gap-4">
        <div className="w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600 dark:text-amber-400">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        <div className="flex-1 space-y-3">
          {paragraphs.map((para, i) => (
            <p key={i} className="text-slate-700 dark:text-slate-300 leading-relaxed text-[15px]">
              {renderInlineMarkdown(para)}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ReflectionChat({
  learnerId,
  moduleId,
  partId,
  agentConfig,
  onSummaryExtracted,
  onComplete,
  onSkip,
}: ReflectionChatProps) {
  const { messages, streamingMessage, sendMessage, isLoading } = useChat({
    learnerId,
    moduleId,
    partId,
    phase: 'reflection',
  });

  const [reflectionComplete, setReflectionComplete] = useState(false);

  // Extract summaries from streaming message
  useEffect(() => {
    if (streamingMessage) {
      const { summaries } = extractSummaries(streamingMessage);
      if (summaries.length > 0) {
        summaries.forEach(summary => onSummaryExtracted(summary));
        setReflectionComplete(true);
      }
    }
  }, [streamingMessage, onSummaryExtracted]);

  // Check existing messages for summaries (in case of page reload)
  useEffect(() => {
    messages.forEach(msg => {
      if (msg.role === 'assistant') {
        const { summaries } = extractSummaries(msg.content);
        if (summaries.length > 0) {
          summaries.forEach(summary => onSummaryExtracted(summary));
          setReflectionComplete(true);
        }
      }
    });
  }, [messages, onSummaryExtracted]);

  // Clean messages to remove summary tags before display
  const cleanedMessages = messages.map(msg => {
    if (msg.role === 'assistant') {
      const { cleanedMessage } = extractSummaries(msg.content);
      return { ...msg, content: cleanedMessage };
    }
    return msg;
  });

  const cleanedStreamingMessage = streamingMessage
    ? extractSummaries(streamingMessage).cleanedMessage
    : '';

  return (
    <div className="flex flex-col h-full">
      {/* Welcome Message — clean, with markdown */}
      {messages.length === 0 && agentConfig.welcomeMessage && (
        <WelcomeMessage text={agentConfig.welcomeMessage} />
      )}

      {/* Subtle divider when welcome is showing */}
      {messages.length === 0 && (
        <div className="border-t border-slate-100 dark:border-slate-700/50" />
      )}

      {/* Messages */}
      <div className="flex-1 min-h-0">
        <ChatMessageList
          messages={cleanedMessages}
          streamingMessage={cleanedStreamingMessage}
          isLoading={isLoading}
        />
      </div>

      {/* Input or Completion */}
      {reflectionComplete ? (
        <div className="p-6 text-center border-t border-slate-100 dark:border-slate-700/50">
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5 mb-4">
            <p className="text-emerald-800 dark:text-emerald-200 font-medium text-sm">
              Reflection complete — your insights have been captured.
            </p>
          </div>
          <button
            onClick={onComplete}
            className="px-8 py-2.5 accent-bg hover:opacity-90 active:scale-[0.98] text-white font-medium rounded-lg transition-all text-sm"
          >
            Continue →
          </button>
        </div>
      ) : (
        <div className="border-t border-slate-100 dark:border-slate-700/50">
          <ChatInput
            onSendMessage={sendMessage}
            disabled={isLoading}
            placeholder="Share your thoughts..."
          />
          <div className="flex justify-end px-4 pb-3">
            <button
              onClick={onSkip}
              className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            >
              Skip reflection →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
